import React, { useState, useEffect, useMemo, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFile,
  faInfoCircle,
  faCheckCircle,
  faSpinner,
  faExclamationTriangle,
  faEye,
  faClock,
  faCircle,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { Avatar } from "@/components/ui/Avatar";
import WorkerCardDialog from "../worker/WorkerCardDialog";
import { TickerFeed } from "@/components/scenarios/components/TickerFeed";
import { formatDistanceToNow } from "date-fns";
import { AgenticAPI, WorkerAniAPI } from "@/services/api";
import type {
  Activity as AgenticActivityType,
  PodTemplate as PodTemplateType,
  PodInstance as PodInstanceType,
  CallToAction as CallToActionType,
} from "@/services/api/agentic/types";
import { PlannrTracker } from "../PlannrTracker";
import { useParams, useNavigate, Link } from "react-router-dom";
import LoadingPlaceholder from "../ui/loading-placeholder/LoadingPlaceholder";
import { LoadingIndicator } from "../indicators/LoadingIndicator";
import WorkerAvatar from "../worker/WorkerAvatar";
import { Badge } from "../ui/badge/Badge";
import type { WorkerCard } from "@/services/api/worker-ani/types";
import Logo from "../Logo";
import { LogsOverview } from "./LogsOverview";
import {
  Table,
  TableBody,
  TableRow,
  TableCell,
} from "@/components/ui/table/Table";
import Drawer from "@/components/ui/drawer";
import { CallToAction } from "./CallToAction";
import { Button } from "@/components/ui/button/Button";
import { useOsActionsOptional } from "@/components/ui/os-layout";
import {
  usePodTemplateWebSocket,
  type PodInstancePayload,
  type CtaPayload,
} from "@/hooks/usePodTemplateWebSocket";

export type ActivityLogItem = {
  id: string;
  description?: string;
  timestamp: string;
  worker_id?: string;
  metadata?: Record<string, any>;
  actor?: { name?: string };
  [key: string]: any;
};

const mapAgenticActivityToLogItem = (
  activity: AgenticActivityType
): ActivityLogItem => {
  return {
    id: activity._id,
    description: activity.description,
    timestamp: activity.timestamp,
    worker_id: activity.assignee?.id,
    metadata: activity.metadata,
    actor: activity.assignee ? { name: activity.assignee.name } : undefined,
  };
};

// Define a type for Active Cases, which are PodInstances
export type ActiveCaseType = PodInstanceType;

// Helper function to format status strings
const formatStatus = (status: string | undefined | null): string => {
  if (!status) {
    return "No status";
  }
  return status
    .toLowerCase()
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Helper function to get badge color from status using hash
const getBadgeColor = (
  status: string
):
  | "red"
  | "amber"
  | "emerald"
  | "blue"
  | "purple"
  | "pink"
  | "teal"
  | "orange" => {
  const colors: Array<
    "red" | "amber" | "emerald" | "blue" | "purple" | "pink" | "teal" | "orange"
  > = ["red", "amber", "emerald", "blue", "purple", "pink", "teal", "orange"];
  const hash = status.split("").reduce((a, b) => a + b.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

// Helper function to get progress bar color based on status and priority
const getProgressBarColor = (
  status: string,
  hasCallToAction: boolean,
  ctaPriority?: string
): string => {
  const statusLower = status.toLowerCase();

  if (statusLower.includes("completed") || statusLower === "complete") {
    return "bg-green-500";
  } else if (statusLower.includes("review")) {
    // Use CTA priority color if available, otherwise use default amber
    if (ctaPriority) {
      const priorityLower = ctaPriority.toLowerCase();
      if (priorityLower === "high") {
        return "bg-red-400";
      } else if (priorityLower === "medium") {
        return "bg-yellow-400";
      } else if (priorityLower === "low") {
        return "bg-green-500";
      }
    }
    return "bg-amber-400";
  } else if (statusLower.includes("blocked") || hasCallToAction) {
    return "bg-red-400";
  } else if (
    statusLower.includes("scheduled") ||
    statusLower.includes("schedule")
  ) {
    return "bg-gray-500";
  } else {
    // Default for in-progress or unknown
    return "bg-indigo-500";
  }
};

// Helper to render status badge (matches TaskChecklist style)
const renderStatusBadge = (
  status: string,
  hasCallToAction: boolean,
  showArrow: boolean = false,
  ctaPriority?: string,
  loadingCallToActions: boolean = false
) => {
  let bgColor = "bg-slate-100 dark:bg-slate-900/20";
  let textColor = "text-slate-600 dark:text-slate-400";
  let icon = faCircle;

  const statusLower = status.toLowerCase();
  const isInProgress = statusLower.includes("progress");
  const isReview = statusLower.includes("review");

  // Check status based on lowercase comparison for more flexibility
  if (statusLower.includes("completed") || statusLower === "complete") {
    bgColor = "bg-green-500/20";
    textColor = "text-green-600 dark:text-green-400";
    icon = faCheckCircle;
  } else if (isInProgress) {
    bgColor = "bg-slate-100 dark:bg-slate-900/20";
    textColor = "text-slate-600 dark:text-slate-400";
    icon = faSpinner;
  } else if (isReview) {
    // Use CTA priority color if available, otherwise use default amber
    if (ctaPriority && !loadingCallToActions) {
      const priorityLower = ctaPriority.toLowerCase();
      if (priorityLower === "high") {
        bgColor = "bg-red-500/20";
        textColor = "text-red-600 dark:text-red-400";
        icon = faExclamationTriangle;
      } else if (priorityLower === "medium") {
        bgColor = "bg-yellow-500/20";
        textColor = "text-yellow-600 dark:text-yellow-400";
        icon = faEye;
      } else if (priorityLower === "low") {
        bgColor = "bg-green-500/20";
        textColor = "text-green-600 dark:text-green-400";
        icon = faEye;
      } else {
        bgColor = "bg-amber-500/20";
        textColor = "text-amber-600 dark:text-amber-400";
        icon = faEye;
      }
    } else {
      bgColor = "bg-amber-500/20";
      textColor = "text-amber-600 dark:text-amber-400";
      icon = faEye;
    }
  } else if (
    statusLower.includes("scheduled") ||
    statusLower.includes("schedule")
  ) {
    bgColor = "bg-gray-500/20";
    textColor = "text-gray-600 dark:text-gray-400";
    icon = faClock;
  } else if (statusLower.includes("blocked") || hasCallToAction) {
    bgColor = "bg-red-500/20";
    textColor = "text-red-600 dark:text-red-400";
    icon = hasCallToAction ? faExclamationTriangle : faCircle;
  }

  return (
    <span
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-medium ${bgColor} ${textColor}`}
    >
      {isInProgress ? (
        <LoadingIndicator
          dotSize={2}
          dotCount={3}
          showText={false}
          dotSpacing={1}
        />
      ) : (
        <FontAwesomeIcon icon={icon} />
      )}
      {formatStatus(status)}
      {showArrow && <span>&rarr;</span>}
    </span>
  );
};

interface PodDashboardProps {
  podInstanceName?: string;
  templateId?: string;
}

export function PodDashboard({ podInstanceName = "Cases", templateId: propTemplateId }: PodDashboardProps) {
  const { id: paramsTemplateId } = useParams<{ id: string }>();
  const templateIdFromRoute = propTemplateId || paramsTemplateId;
  const navigate = useNavigate();
  const osActions = useOsActionsOptional();

  /**
   * Navigate to a pod instance - opens a new window in OS mode,
   * or navigates via URL in classic mode.
   */
  const navigateToInstance = useCallback(
    (instance: PodInstanceType | ActiveCaseType) => {
      const instanceId = instance.id || (instance as any)._id;
      const instanceName = instance.name || `${podInstanceName}`;
      
      if (osActions && templateIdFromRoute) {
        osActions.openWindow({
          appId: "pod-instance",
          title: instanceName,
          params: {
            instanceId,
            templateId: templateIdFromRoute,
          },
        });
      } else {
        navigate(`/pod/${templateIdFromRoute}/${instanceId}`);
      }
    },
    [osActions, navigate, templateIdFromRoute, podInstanceName]
  );

  // Helper to pluralize the instance name for display
  const pluralizedInstanceName = podInstanceName.endsWith("s")
    ? podInstanceName
    : `${podInstanceName}s`;

  const [isWorkerCardOpen, setIsWorkerCardOpen] = useState(false);
  const [selectedWorkerId, setSelectedWorkerId] = useState<string | null>(null);
  const [selectedWorkerCard, setSelectedWorkerCard] =
    useState<WorkerCard | null>(null);
  const [recentActivities, setRecentActivities] = useState<ActivityLogItem[]>(
    []
  );
  const [loadingActivities, setLoadingActivities] = useState(true);
  const [podTemplate, setPodTemplate] = useState<PodTemplateType | null>(null);
  const [loadingPodTemplate, setLoadingPodTemplate] = useState(true);
  const [showAllRules, setShowAllRules] = useState(false);
  const [activeCases, setActiveCases] = useState<ActiveCaseType[]>([]);
  const [loadingActiveCases, setLoadingActiveCases] = useState(true);
  const [workerCards, setWorkerCards] = useState<WorkerCard[]>([]);
  const [loadingWorkerCards, setLoadingWorkerCards] = useState(true);
  const [isCtaDrawerOpen, setIsCtaDrawerOpen] = useState(false);
  const [selectedInstance, setSelectedInstance] =
    useState<ActiveCaseType | null>(null);
  const [instanceCallToActions, setInstanceCallToActions] = useState<
    CallToActionType[]
  >([]);
  const [allCallToActions, setAllCallToActions] = useState<CallToActionType[]>(
    []
  );
  const [loadingAllCtas, setLoadingAllCtas] = useState(true);

  // WebSocket integration for real-time updates
  const { isConnected } = usePodTemplateWebSocket({
    templateId: templateIdFromRoute,
    onInstanceCreated: useCallback((instance: PodInstancePayload) => {
      const newInstance: ActiveCaseType = {
        id: instance.id,
        name: instance.name,
        template_id: templateIdFromRoute || "",
        owner_id: "",
        team_ids: [],
        client_id: "",
        state: {
          ...instance.state,
          call_to_action: "",
        },
        tasks: [],
        data: {},
        log: [],
        rules: [],
        variables: {},
        metadata: {},
        created_at: instance.created_at || new Date().toISOString(),
        updated_at: instance.updated_at || new Date().toISOString(),
      };
      setActiveCases((prev) => [newInstance, ...prev]);
    }, [templateIdFromRoute]),
    onInstanceUpdated: useCallback((instance: PodInstancePayload) => {
      setActiveCases((prev) =>
        prev.map((c) =>
          c.id === instance.id || (c as any)._id === instance.id
            ? {
                ...c,
                state: { ...c.state, ...instance.state },
                name: instance.name,
                updated_at: instance.updated_at || c.updated_at,
              }
            : c
        )
      );
    }, []),
    onInstanceDeleted: useCallback((instanceId: string) => {
      setActiveCases((prev) =>
        prev.filter((c) => c.id !== instanceId && (c as any)._id !== instanceId)
      );
    }, []),
    onCtaCreated: useCallback((cta: CtaPayload) => {
      const newCta: CallToActionType = {
        id: cta.id,
        _id: cta.id,
        pod_instance_id: cta.pod_instance_id,
        task_id: "",
        category: cta.category,
        remedy_type: "button_action",
        message: cta.message,
        priority: cta.priority,
        metadata: {},
        status: cta.status,
      };
      setAllCallToActions((prev) => [newCta, ...prev]);
    }, []),
    onCtaCompleted: useCallback((ctaId: string) => {
      setAllCallToActions((prev) => prev.filter((c) => c.id !== ctaId && c._id !== ctaId));
    }, []),
    onCtaCancelled: useCallback((ctaId: string) => {
      setAllCallToActions((prev) => prev.filter((c) => c.id !== ctaId && c._id !== ctaId));
    }, []),
    enabled: !!templateIdFromRoute,
  });

  const handleOpenWorkerCard = (workerId: string) => {
    const workerCard = workerCards.find((card) => card._id === workerId);
    setSelectedWorkerId(workerId);
    setSelectedWorkerCard(workerCard || null);
    setIsWorkerCardOpen(true);
  };

  const handleCloseWorkerCard = () => {
    setSelectedWorkerId(null);
    setSelectedWorkerCard(null);
    setIsWorkerCardOpen(false);
  };

  const handleOpenCtaDrawer = async (
    instance: ActiveCaseType,
    e: React.MouseEvent
  ) => {
    e.stopPropagation(); // Prevent navigation when clicking the button
    setSelectedInstance(instance);
    setIsCtaDrawerOpen(true);

    // Filter the already-fetched CTAs for this instance
    const instanceCtas = allCallToActions.filter(
      (cta: any) =>
        cta.pod_instance_id === (instance.id || (instance as any)._id)
    );
    setInstanceCallToActions(instanceCtas);
  };

  const handleCloseCtaDrawer = () => {
    setIsCtaDrawerOpen(false);
    setSelectedInstance(null);
    setInstanceCallToActions([]);
  };

  useEffect(() => {
    const fetchActivities = async () => {
      setLoadingActivities(true);
      try {
        const response = await AgenticAPI.getAllAgenticActivities({
          limit: "10",
          template_id: templateIdFromRoute,
        });
        let fetchedActivities: AgenticActivityType[] = [];
        if (response && (response as any).results) {
          fetchedActivities = (response as any).results;
        } else if (Array.isArray(response)) {
          fetchedActivities = response as any[];
        }
        setRecentActivities(fetchedActivities.map(mapAgenticActivityToLogItem));
      } catch (error) {
        console.error("Error fetching recent agentic activities:", error);
        setRecentActivities([]);
      } finally {
        setLoadingActivities(false);
      }
    };
    fetchActivities();

    const fetchPodTemplate = async () => {
      if (!templateIdFromRoute) {
        console.warn("Template ID not found in route for PodDashboard");
        setLoadingPodTemplate(false);
        setPodTemplate(null);
        return;
      }
      setLoadingPodTemplate(true);
      try {
        const template = await AgenticAPI.getPodTemplate(templateIdFromRoute);
        setPodTemplate(template);
      } catch (error) {
        console.error("Error fetching pod template:", error);
        setPodTemplate(null);
      } finally {
        setLoadingPodTemplate(false);
      }
    };
    fetchPodTemplate();

    const fetchActiveCases = async () => {
      setLoadingActiveCases(true);
      try {
        // Fetch all instances for this template (same as Actions tab)
        const response = await AgenticAPI.getPodInstances({
          template_id: templateIdFromRoute,
          limit: 500,
        });
        let fetchedCases: PodInstanceType[] = [];

        if (response && (response as any).results) {
          fetchedCases = (response as any).results; // For paginated response
        } else if (Array.isArray(response)) {
          fetchedCases = response as any[]; // For direct array response
        }
        setActiveCases(fetchedCases);
      } catch (error) {
        console.error("Error fetching active cases:", error);
        setActiveCases([]);
      } finally {
        setLoadingActiveCases(false);
      }
    };
    fetchActiveCases();

    const fetchCallToActions = async () => {
      setLoadingAllCtas(true);
      try {
        const ctas = await AgenticAPI.getCallToActionsByTemplate(
          templateIdFromRoute || "",
          { limit: 500 }
        );
        // Filter to only show active CTAs (live and processing), exclude completed and cancelled
        const activeCtas = ctas.filter(
          (cta: any) => cta.status === "live" || cta.status === "processing"
        );
        setAllCallToActions(activeCtas);
      } catch (error) {
        console.error("Error fetching call-to-actions:", error);
        setAllCallToActions([]);
      } finally {
        setLoadingAllCtas(false);
      }
    };
    fetchCallToActions();
  }, [templateIdFromRoute]);

  // Fetch worker cards when pod template changes
  useEffect(() => {
    const fetchWorkerCards = async () => {
      if (!podTemplate?.workers || podTemplate.workers.length === 0) {
        setWorkerCards([]);
        setLoadingWorkerCards(false);
        return;
      }

      setLoadingWorkerCards(true);
      try {
        // Fetch each worker card individually
        const workerCardPromises = podTemplate.workers.map((workerId) =>
          WorkerAniAPI.getWorkerCard(workerId)
        );
        const fetchedWorkerCards = await Promise.all(workerCardPromises);
        setWorkerCards(fetchedWorkerCards);
      } catch (error) {
        console.error("Error fetching worker cards:", error);
        setWorkerCards([]);
      } finally {
        setLoadingWorkerCards(false);
      }
    };

    fetchWorkerCards();
  }, [podTemplate]);

  // Calculate dynamic counts
  const activeCasesCount = activeCases.length;

  // Create a Map for O(1) CTA lookups by instance ID
  const ctaByInstanceId = useMemo(() => {
    const map = new Map<string, any>();
    allCallToActions.forEach((cta: any) => {
      if (cta.pod_instance_id) {
        // Store first CTA for each instance (could be extended to store all CTAs if needed)
        if (!map.has(cta.pod_instance_id)) {
          map.set(cta.pod_instance_id, cta);
        }
      }
    });
    return map;
  }, [allCallToActions]);

  // Filter instances that have call-to-actions (same as alerts/actions tab)
  // Sort by most recent (updated_at) first
  const needsReviewCases = useMemo(() => {
    return activeCases
      .filter((instance) => {
        const instanceId = instance.id || (instance as any)._id;
        return ctaByInstanceId.has(instanceId);
      })
      .sort(
        (a, b) =>
          new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      );
  }, [activeCases, ctaByInstanceId]);
  const needsReviewCount = needsReviewCases.length;

  // Sort cases by most recently updated first and take top 5 for "Latest Cases" section
  const latestCreatedCases = [...activeCases]
    .sort(
      (a, b) =>
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    )
    .slice(0, 5);

  return (
    <div className="flex h-full flex-col p-4 text-slate-900 dark:text-white">
      <div className="grid grid-cols-4 grid-rows-[auto_1fr] gap-5 flex-1">
        <div className="col-span-full">
          <div className="w-full max-w-full text-sm overflow-hidden relative">
            <TickerFeed
              title="Recent Activities"
              items={recentActivities.map((activity) => (
                <div
                  key={activity.id}
                  className="inline-flex items-baseline gap-2 text-sm"
                >
                  <span className="text-slate-700 dark:text-slate-300">
                    {activity.description ?? "No description"}
                  </span>
                  <span className="text-slate-400 dark:text-slate-400">
                    {formatDistanceToNow(new Date(activity.timestamp), {
                      addSuffix: true,
                    })}
                  </span>
                </div>
              ))}
              scrollSpeed={0.5}
            />
          </div>
        </div>
        <div className="col-span-1 flex flex-col">
          <div className="bg-white dark:bg-slate-800 rounded-3xl  p-6 h-full">
            <h2 className="font-semibold text-xl text-slate-800 dark:text-slate-200 mb-4">
              Project Scope
            </h2>

            <div className="space-y-6">
              {loadingPodTemplate ? (
                <LoadingPlaceholder pattern="text" count={2} />
              ) : podTemplate && podTemplate.objective ? (
                <p className="text-slate-600 dark:text-slate-300">
                  {podTemplate.objective}
                </p>
              ) : (
                <p className="text-slate-500">
                  No objective found for this template.
                </p>
              )}

              <div>
                <div className="font-semibold text-slate-800 dark:text-slate-200 mb-3">
                  Available Workers
                </div>
                <div className="flex flex-wrap gap-3">
                  {loadingWorkerCards ? (
                    <LoadingPlaceholder pattern="list" count={3} />
                  ) : workerCards.length > 0 ? (
                    workerCards.map((workerCard) => (
                      <button
                        key={workerCard._id}
                        onClick={() => handleOpenWorkerCard(workerCard._id)}
                        className="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                      >
                        <WorkerAvatar seed={workerCard.name} size={40} />
                        <span className="text-xs text-slate-600 dark:text-slate-400 font-medium text-center max-w-16 truncate">
                          {workerCard.name}
                        </span>
                      </button>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500">
                      No workers available
                    </p>
                  )}
                </div>
                <div className="mt-6">
                  <div className="font-semibold text-slate-800 dark:text-white">
                    Rules
                  </div>
                  <div className="mt-2 flex flex-col gap-2 text-slate-600 dark:text-slate-300">
                    {podTemplate?.rules && podTemplate.rules.length > 0 ? (
                      <>
                        <ul className="list-disc list-outside pl-5 space-y-1">
                          {(showAllRules
                            ? podTemplate.rules
                            : podTemplate.rules.slice(0, 3)
                          ).map((rule: string, index: number) => (
                            <li key={index}>{rule}</li>
                          ))}
                        </ul>
                        {podTemplate.rules.length > 3 && (
                          <button
                            onClick={() => setShowAllRules(!showAllRules)}
                            className="text-left text-slate-500 underline mt-2 cursor-pointer"
                          >
                            {showAllRules ? "Show less" : "Show more"}
                          </button>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-gray-400">No rules defined.</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-span-3 flex flex-col gap-6">
          {/* Activity Logs Widget */}

          <div className="rounded-3xl bg-white dark:bg-slate-800 flex flex-col flex-1">
            {/* <div className="p-6 pb-4">
              <div className="text-5xl font-bold text-slate-900 dark:text-white">
                {loadingActiveCases ? "-" : activeCasesCount}
              </div>
              <h3 className="font-mono font-normal text-slate-500 dark:text-slate-400 mt-1">
                Active {pluralizedInstanceName}
              </h3>
            </div> */}

            <div className="bg-white dark:bg-slate-800 rounded-3xl p-6">
              <LogsOverview
                templateId={templateIdFromRoute}
                podTemplate={podTemplate || undefined}
              />
            </div>
            <div className="flex flex-col p-6">
              <div className="mb-6 flex items-center justify-between font-semibold text-slate-900 dark:text-white">
                <div className="flex items-center gap-3">
                  Actions for your review
                  {/* Connection status indicator */}
                  <div className="flex items-center gap-1.5" data-testid="connection-status">
                    <span
                      className={`h-2 w-2 rounded-full ${
                        isConnected ? "bg-emerald-500" : "bg-slate-500"
                      }`}
                    />
                    <span className="text-xs font-normal text-slate-500">
                      {isConnected ? "Live" : "Connecting..."}
                    </span>
                  </div>
                </div>
                <div className="text-sm text-slate-500">
                  <Link to={`/pod/${templateIdFromRoute}?tab=actions`}>
                    View all
                  </Link>
                </div>
              </div>
              <div className="flex flex-col gap-4 ">
                {loadingActiveCases || loadingAllCtas ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex items-center gap-4 py-3">
                        <div className="h-10 w-10 animate-pulse rounded bg-slate-200 dark:bg-slate-600/30" />
                        <div className="flex-1 space-y-2">
                          <div className="h-4 w-3/4 animate-pulse rounded bg-slate-200 dark:bg-slate-600/30" />
                          <div className="h-3 w-1/2 animate-pulse rounded bg-slate-200 dark:bg-slate-600/30" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : needsReviewCases.length > 0 ? (
                  needsReviewCases.slice(0, 5).map((activeCase, index) => {
                    // Use Map for O(1) CTA lookup instead of O(n) .find()
                    const instanceId = activeCase.id || (activeCase as any)._id;
                    const instanceCta = ctaByInstanceId.get(instanceId);
                    const ctaDescription =
                      instanceCta?.metadata?.description ||
                      instanceCta?.message ||
                      "Action required";

                    // Get priority/severity color
                    const priority = (
                      instanceCta?.priority || ""
                    ).toLowerCase();
                    let priorityColor = "";
                    let bgColor = "";
                    switch (priority) {
                      case "high":
                        priorityColor = "bg-red-400";
                        bgColor = "bg-red-50 dark:bg-red-950/20";
                        break;
                      case "medium":
                        priorityColor = "bg-yellow-400";
                        bgColor = "bg-yellow-50 dark:bg-yellow-950/20";
                        break;
                      case "low":
                        priorityColor = "bg-green-400";
                        bgColor = "bg-green-50 dark:bg-green-950/20";
                        break;
                      default:
                        priorityColor = "bg-slate-400";
                        bgColor = "bg-slate-50 dark:bg-slate-800";
                    }

                    return (
                      <div
                        key={activeCase.id || (activeCase as any)._id || index}
                        className="flex w-full items-center gap-4 rounded-lg border border-slate-200/60 bg-slate-100 px-3 py-2 transition-colors dark:border-transparent dark:bg-slate-700/50"
                      >
                        <div
                          className={`w-1 h-full rounded-full ${priorityColor}`}
                        ></div>
                        <div className="flex-grow">
                          <button
                            onClick={() => navigateToInstance(activeCase)}
                            className="flex items-center gap-3 flex-grow text-left"
                          >
                            <div className="flex-grow min-w-0">
                              <div className="flex items-start gap-2 line-clamp-2">
                                <span className="flex-shrink-0 font-medium text-slate-900 dark:text-slate-100">
                                  {activeCase.name ||
                                    `Unnamed ${podInstanceName}`}
                                </span>
                              </div>
                              <div>
                                <span className="text-sm text-slate-500">
                                  {ctaDescription}
                                </span>
                              </div>
                            </div>
                          </button>
                        </div>
                        <div className="flex-none">
                          <Button
                            color="color"
                            onClick={(e: React.MouseEvent) =>
                              handleOpenCtaDrawer(activeCase, e)
                            }
                          >
                            View Details
                          </Button>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-sm text-gray-500">
                    No {pluralizedInstanceName.toLowerCase()} have outstanding actions.
                  </p>
                )}

                {/* Latest Cases Section */}
                <div className="mb-4 mt-8 flex items-center justify-between font-semibold text-slate-900 dark:text-white">
                  Latest {pluralizedInstanceName}
                  <div className="text-sm text-slate-500">
                    <Link to={`/pod/${templateIdFromRoute}?tab=instance-list`}>
                      View all
                    </Link>
                  </div>
                </div>
                <div>
                  {loadingActiveCases ? (
                    <div className="space-y-3">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="flex items-center gap-4 py-3">
                          <div className="flex-1">
                            <div className="h-4 w-3/4 animate-pulse rounded bg-slate-200 dark:bg-slate-600/30" />
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="h-2 w-16 animate-pulse rounded-full bg-slate-200 dark:bg-slate-600/30" />
                            <div className="h-3 w-8 animate-pulse rounded bg-slate-200 dark:bg-slate-600/30" />
                          </div>
                          <div className="h-7 w-28 animate-pulse rounded-xl bg-slate-200 dark:bg-slate-600/30" />
                        </div>
                      ))}
                    </div>
                  ) : latestCreatedCases.length > 0 ? (
                    <div className="rounded-2xl border border-slate-200/60 bg-slate-100 dark:border-transparent dark:bg-slate-700/50">
                      <Table>
                        <TableBody>
                          {latestCreatedCases.map((activeCase, index) => {
                            const instanceId =
                              activeCase.id || (activeCase as any)._id;
                            const hasCallToActions = allCallToActions.some(
                              (cta: any) => cta.pod_instance_id === instanceId
                            );

                            // Get the highest priority CTA for this instance (only if CTAs are loaded)
                            let highestPriority = "";
                            if (!loadingAllCtas) {
                              const instanceCtas = allCallToActions.filter(
                                (cta: any) => cta.pod_instance_id === instanceId
                              );
                              highestPriority = instanceCtas.reduce(
                                (highest: string, cta: any) => {
                                  const priority = (
                                    cta.priority || ""
                                  ).toLowerCase();
                                  if (priority === "high") return "high";
                                  if (
                                    priority === "medium" &&
                                    highest !== "high"
                                  )
                                    return "medium";
                                  if (
                                    priority === "low" &&
                                    highest !== "high" &&
                                    highest !== "medium"
                                  )
                                    return "low";
                                  return highest;
                                },
                                ""
                              );
                            }

                            const progress =
                              (activeCase as any).progress_percentage ??
                              (activeCase as any).state?.progress_percentage ??
                              0.0;  // Default to 0 if no progress available
                            const progressColor = getProgressBarColor(
                              hasCallToActions
                                ? "Needs Review"
                                : activeCase.state?.current_status || "Unknown",
                              hasCallToActions,
                              highestPriority
                            );

                            return (
                              <TableRow
                                key={
                                  activeCase.id ||
                                  (activeCase as any)._id ||
                                  index
                                }
                                onClick={() => navigateToInstance(activeCase)}
                                className="cursor-pointer"
                              >
                                <TableCell className="text-slate-900 dark:text-slate-100 max-w-0">
                                  <div className="flex flex-col gap-1 min-w-0 max-w-full">
                                    <div className="truncate text-sm overflow-hidden text-ellipsis whitespace-nowrap">
                                      {activeCase.name ||
                                        `Unnamed ${podInstanceName}`}
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell
                                  className="text-center w-32 flex-shrink-0"
                                  onClick={
                                    hasCallToActions
                                      ? (e: React.MouseEvent) =>
                                          handleOpenCtaDrawer(activeCase, e)
                                      : undefined
                                  }
                                >
                                  <div
                                    className={`flex items-center justify-end gap-2 ${hasCallToActions ? "cursor-pointer hover:opacity-80 transition-opacity" : ""}`}
                                  >
                                    <div className="h-2 w-16 flex-shrink-0 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-900/20">
                                      <div
                                        className={`h-full ${progressColor} transition-all duration-300 rounded-full`}
                                        style={{
                                          width: `${Math.round(progress * 100)}%`,
                                        }}
                                      />
                                    </div>
                                    <span className="text-xs font-mono text-slate-600 dark:text-slate-400 w-8 text-right flex-shrink-0">
                                      {Math.round(progress * 100)}%
                                    </span>
                                  </div>
                                </TableCell>
                                <TableCell
                                  className="text-center w-40 flex-shrink-0"
                                  onClick={
                                    hasCallToActions
                                      ? (e: React.MouseEvent) =>
                                          handleOpenCtaDrawer(activeCase, e)
                                      : undefined
                                  }
                                >
                                  <div
                                    className={`flex items-center justify-center ${hasCallToActions ? "cursor-pointer hover:opacity-80 transition-opacity" : ""}`}
                                  >
                                    {renderStatusBadge(
                                      hasCallToActions
                                        ? "Needs Review"
                                        : activeCase.state?.current_status ||
                                            "Unknown",
                                      hasCallToActions,
                                      hasCallToActions,
                                      highestPriority,
                                      loadingAllCtas
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">
                      No recent {pluralizedInstanceName.toLowerCase()} found.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* <div className="col-span-1 flex flex-col gap-6">


          <div className="rounded-3xl bg-white dark:bg-slate-800 flex flex-col flex-1">
            <div className="p-6 pb-4">
              <div className="text-5xl font-bold text-slate-900 dark:text-white">
                Latest
              </div>
              <h3 className="font-mono font-normal text-slate-500 dark:text-slate-400 mt-1">
                Activity
              </h3>
            </div>
            <div className="p-6 pt-0">
              {loadingActivities ? (
                <LoadingPlaceholder pattern="list" count={3} />
              ) : recentActivities && recentActivities.length > 0 ? (
                <>
                  <div className="font-semibold flex items-center justify-between mb-4">
                    Recent Activity{" "}
                    <div className="text-sm text-slate-500">
                      {Math.min(recentActivities.length, 4)}
                    </div>
                  </div>
                  <div className="space-y-3">
                    {recentActivities.slice(0, 4).map((activity) => (
                      <div
                        key={activity.id}
                        className="bg-slate-50 dark:bg-slate-700 rounded-lg p-3"
                      >
                        <div className="flex items-start gap-3">
                          <div>
                            <span className="flex h-8 w-8 items-center justify-center">
                              {activity.worker_id === "ani" ||
                              activity.actor?.name?.toLowerCase() === "ani" ? (
                                <Logo iconOnly className="h-8 w-8" />
                              ) : (
                                <WorkerAvatar
                                  seed={
                                    activity.actor?.name ||
                                    activity.worker_id ||
                                    "system"
                                  }
                                  size={32}
                                />
                              )}
                            </span>
                          </div>
                          <div className="flex-grow min-w-0">
                            <div className="text-sm text-slate-800 dark:text-slate-200 mb-1">
                              {activity.description || "Activity occurred"}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                              <span className="font-medium">
                                {activity.actor?.name || "System"}
                              </span>
                              <span>•</span>
                              <span>
                                {formatDistanceToNow(
                                  new Date(activity.timestamp),
                                  { addSuffix: true }
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-sm text-gray-500">
                  No recent activities found.
                </p>
              )}
            </div>
          </div>
        </div> */}
      </div>

      {selectedWorkerId && (
        <WorkerCardDialog
          open={isWorkerCardOpen}
          onClose={handleCloseWorkerCard}
          workerId={selectedWorkerId}
          rules={selectedWorkerCard?.rules}
        />
      )}

      {/* Drawer for displaying call-to-actions */}
      <Drawer
        isOpen={isCtaDrawerOpen}
        onClose={handleCloseCtaDrawer}
        title={selectedInstance?.name || "Actions Required"}
        width="max-w-2xl"
      >
        <div className="px-6 pb-6">
          <div>
            {selectedInstance?.ani_update && (
              <div className="text-slate-900 dark:text-slate-100 mb-8">
                {selectedInstance.ani_update}
              </div>
            )}
          </div>

          {instanceCallToActions.length > 0 ? (
            <div className="space-y-3 text-white">
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0 space-y-4">
                  <div className="grid grid-cols-1 gap-4 text-sm">
                    {instanceCallToActions.map((action, index) => (
                      <div
                        key={action.id || index}
                        className="p-4 flex flex-col rounded-xl space-y-4 transition-all duration-200 bg-white dark:bg-slate-700 hover:shadow-sm"
                      >
                        <CallToAction action={action} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-slate-500 dark:text-slate-400">
                No actions required at this time.
              </p>
            </div>
          )}
        </div>
      </Drawer>
    </div>
  );
}
